import { CircularProgress } from '@mui/material';
import UPMultiselectDropdown from 'components/app-container/multiselect-dropdown';
import UPButton from 'components/button';
import { UPToggleButton } from 'components/button/styles';
import UPButtonGroup from 'components/button/UPGroupButton';
import UPDropdownWithSearch from 'components/dropdown-with-search/UPDropdownWithSearch';
import { UPDropdownItem } from 'components/dropdown/types';
import ErrorBar from 'components/error-bar';
import UPSidePanel, { UPSidePanelSection } from 'components/side-panel';
import { ScopeLevel } from 'constants/ScopeLevel';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__ADD_EXCEPTION,
  CONTRACT_COMPLIANCE__EDIT_EXCEPTION,
  CONTRACT_COMPLIANCE__REMOVE_EXCEPTION,
  CONTRACT_COMPLIANCE__SCOPE_AGENCY_HINT,
  CONTRACT_COMPLIANCE__SCOPE_ENTITIES,
  CONTRACT_COMPLIANCE__SCOPE_ENTITY,
  CONTRACT_COMPLIANCE__SCOPE_LEVEL,
  CONTRACT_COMPLIANCE__TAKES_EFFECT_HINT,
  GENERAL__CLOSE,
  GENERAL__RETRY,
  GENERAL__SAVE_BUTTON,
  GENERAL__SELECT,
} from 'translations/constants';
import { SCOPE_LEVEL_LABEL_KEYS, SCOPE_LEVELS } from './constants';
import { UseRuleExceptionPanelResult } from './hooks/useRuleExceptionPanel';
import JustificationFields from './JustificationFields';
import { HintText, PanelErrorRow, RemoveExceptionLink } from './styles';

interface RuleExceptionPanelProps {
  panel: UseRuleExceptionPanelResult;
  title: string;
  testIdPrefix: string;
  hintKey: string;
  valueFields?: ReactNode;
}

/** An exception is binary: the panel only chooses its scope. Entities are always picked, never typed. */
const RuleExceptionPanel = ({
  panel,
  title,
  testIdPrefix,
  hintKey,
  valueFields,
}: RuleExceptionPanelProps): JSX.Element => {
  const [t] = useTranslation();
  const { mode, rule, status, scopeLevel, selectedEntityIds, scopeEntities, fieldError, isSaving } = panel;
  const isBusy = isSaving || panel.justification.isUploading;
  const entitiesStatus = scopeEntities.statusByLevel[scopeLevel];
  const items: UPDropdownItem[] = useMemo(() => scopeEntities.toDropdownItems(scopeLevel), [scopeEntities, scopeLevel]);
  const selectedItems = useMemo(
    () => items.filter(item => selectedEntityIds.includes(String(item.value))),
    [items, selectedEntityIds],
  );

  const renderEntityPicker = () => {
    if (scopeLevel === ScopeLevel.AGENCY) {
      return (
        <HintText data-testid={`${testIdPrefix}-agency-hint`}>{t(CONTRACT_COMPLIANCE__SCOPE_AGENCY_HINT)}</HintText>
      );
    }
    if (entitiesStatus === 'error' && scopeEntities.errorMessage) {
      return (
        <PanelErrorRow data-testid={`${testIdPrefix}-entities-error`}>
          <ErrorBar message={scopeEntities.errorMessage} />
          <UPButton
            text={t(GENERAL__RETRY)}
            onClick={panel.retryEntities}
            outlined
            testId={`${testIdPrefix}-entities-retry`}
          />
        </PanelErrorRow>
      );
    }
    if (entitiesStatus !== 'ready')
      return <CircularProgress size={20} data-testid={`${testIdPrefix}-entities-loading`} />;

    if (mode === 'edit') {
      // One exception is one entity: changing it is a single choice.
      return (
        <UPDropdownWithSearch
          key={scopeLevel}
          label={t(CONTRACT_COMPLIANCE__SCOPE_ENTITY)}
          items={items}
          value={selectedEntityIds[0] ?? ''}
          onChange={value => panel.selectEntities(value ? [String(value)] : [])}
          error={!!fieldError}
          helperText={fieldError ?? ''}
          disabled={isSaving}
          required
          testId={`${testIdPrefix}-entity`}
        />
      );
    }
    return (
      <UPMultiselectDropdown
        key={scopeLevel}
        label={t(CONTRACT_COMPLIANCE__SCOPE_ENTITIES)}
        placeholder={t(GENERAL__SELECT)}
        items={items}
        value={selectedItems}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        handleChange={(value: UPDropdownItem[] | null) =>
          panel.selectEntities((value ?? []).map(item => String(item.value)))
        }
        withCheckbox
        disabled={isSaving}
        required
        error={!!fieldError}
        helperText={fieldError ?? ''}
        testId={`${testIdPrefix}-entities`}
      />
    );
  };

  return (
    <UPSidePanel
      open={status !== 'closed'}
      title={title}
      // Two-line header like the feature access panel, so the card starts at the same height.
      subtitle={t(mode === 'add' ? CONTRACT_COMPLIANCE__ADD_EXCEPTION : CONTRACT_COMPLIANCE__EDIT_EXCEPTION)}
      onClose={panel.close}
      testId={`${testIdPrefix}-panel`}
      footer={
        <>
          <UPButton
            text={t(GENERAL__SAVE_BUTTON)}
            onClick={panel.requestSave}
            disabled={status !== 'ready' || isBusy || !panel.isComplete}
            testId={`${testIdPrefix}-save`}
          />
          <UPButton text={t(GENERAL__CLOSE)} onClick={panel.close} outlined testId={`${testIdPrefix}-close`} />
        </>
      }
    >
      {status === 'loading' && <CircularProgress data-testid={`${testIdPrefix}-panel-loading`} />}

      {status === 'loadError' && panel.loadErrorMessage && (
        <PanelErrorRow data-testid={`${testIdPrefix}-panel-load-error`}>
          <ErrorBar message={panel.loadErrorMessage} />
          <UPButton
            text={t(GENERAL__RETRY)}
            onClick={panel.retryLoad}
            outlined
            testId={`${testIdPrefix}-panel-retry`}
          />
        </PanelErrorRow>
      )}

      {panel.saveErrorMessage && (
        <PanelErrorRow data-testid={`${testIdPrefix}-save-error`}>
          <ErrorBar message={panel.saveErrorMessage} />
        </PanelErrorRow>
      )}

      {status === 'ready' && rule && (
        <UPSidePanelSection
          title={t(mode === 'add' ? CONTRACT_COMPLIANCE__ADD_EXCEPTION : CONTRACT_COMPLIANCE__EDIT_EXCEPTION)}
          testId={`${testIdPrefix}-details`}
        >
          <>
            <UPButtonGroup
              label={t(CONTRACT_COMPLIANCE__SCOPE_LEVEL)}
              value={scopeLevel}
              // MUI's exclusive group reports null when the pressed option is clicked again; keep the current value.
              onChange={value => value && panel.setScopeLevel(value as ScopeLevel)}
              disabled={isSaving}
            >
              {SCOPE_LEVELS.map(level => (
                <UPToggleButton key={level} value={level} data-testid={`${testIdPrefix}-scope-level-${level}`}>
                  {t(SCOPE_LEVEL_LABEL_KEYS[level])}
                </UPToggleButton>
              ))}
            </UPButtonGroup>
            {renderEntityPicker()}
            {valueFields}
            <HintText>{t(hintKey)}</HintText>
            <HintText>{t(CONTRACT_COMPLIANCE__TAKES_EFFECT_HINT)}</HintText>
            <JustificationFields justification={panel.justification} disabled={isSaving} testIdPrefix={testIdPrefix} />
            {mode === 'edit' && (
              <RemoveExceptionLink
                type="button"
                onClick={panel.requestRemove}
                disabled={status !== 'ready' || isBusy}
                data-testid={`${testIdPrefix}-remove`}
              >
                {t(CONTRACT_COMPLIANCE__REMOVE_EXCEPTION)}
              </RemoveExceptionLink>
            )}
          </>
        </UPSidePanelSection>
      )}
    </UPSidePanel>
  );
};

RuleExceptionPanel.defaultProps = {
  valueFields: null,
};

export default RuleExceptionPanel;
